import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Check, X, ArrowLeft, Send, RotateCcw, TrendingUp } from "lucide-react";
import { useConfig } from "@/hooks/useConfig";
import { fmtDate, istToday } from "@/lib/dateFormat";

const STATUS_COLORS = { draft: "bg-zinc-200 text-zinc-700", submitted_to_admin: "bg-amber-100 text-amber-800", returned_by_admin: "bg-orange-100 text-orange-800", approved: "bg-green-100 text-green-800" };
const STATUS_LABELS = { draft: "Draft", submitted_to_admin: "Submitted", returned_by_admin: "Returned", approved: "Approved" };
const TL_COLORS = { green: "bg-green-100 text-green-800", amber: "bg-amber-100 text-amber-800", red: "bg-red-100 text-red-800" };
const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function SalesTargetsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roles = user?.roles || [];
  const perms = user?.permissions || [];
  const isAdmin = roles.includes("Admin") || roles.includes("admin");
  const isMgr = roles.includes("Sales Manager");
  const canManage = isAdmin || perms.includes("targets.manage");
  const canViewPnl = isAdmin || perms.includes("targets.view_pnl");

  const { getValues } = useConfig(["channel_types"]);
  const TRADE_FORMATS = getValues("channel_types");

  const [tab, setTab] = useState("targets");
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [managers, setManagers] = useState([]);
  const [createForm, setCreateForm] = useState({ trade_format: "", sales_manager_id: "", period_type: "monthly", period_start: "", period_end: "", target_value: "" });
  const [saving, setSaving] = useState(false);

  // Detail view
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [channelData, setChannelData] = useState([]);
  const [expandedChannels, setExpandedChannels] = useState({});
  const [managerNotes, setManagerNotes] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved"
  const [channelSaveStatus, setChannelSaveStatus] = useState({});
  const [detailMeta, setDetailMeta] = useState({ target_value: 0, grand_total: 0 });

  // Performance
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  // Filters
  const today = istToday();
  const monthStart = today.slice(0, 8) + "01";
  const [filterFrom, setFilterFrom] = useState(monthStart);
  const [filterTo, setFilterTo] = useState(today);
  const [filterTF, setFilterTF] = useState("");

  const loadTargets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTF) params.trade_format = filterTF;
      const { data } = await api.get("/targets", { params });
      setTargets(data);
    } catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  }, [filterTF]);

  const loadPerformance = useCallback(async () => {
    setPerfLoading(true);
    try {
      const params = { period_start: filterFrom, period_end: filterTo };
      if (filterTF) params.trade_format = filterTF;
      const { data } = await api.get("/targets/dashboard", { params });
      setPerfData(data);
    } catch(e) { console.error("Load error:", e); } finally { setPerfLoading(false); }
  }, [filterFrom, filterTo, filterTF]);

  useEffect(() => { loadTargets(); }, [loadTargets]);
  useEffect(() => { if (tab === "performance") loadPerformance(); }, [tab, loadPerformance]);
  useEffect(() => { api.get("/users?status=active").then(r => setManagers((r.data || []).filter(u => (u.roles || []).includes("Sales Manager")))).catch(e => console.error("API error:", e)); }, []);

  const openCreate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const endOfNext = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0);
    setCreateForm({ trade_format: "", sales_manager_id: "", period_type: "monthly", period_start: nextMonth.toISOString().slice(0, 10), period_end: endOfNext.toISOString().slice(0, 10), target_value: "" });
    setShowCreate(true);
  };

  const saveCreate = async () => {
    if (!createForm.trade_format || !createForm.sales_manager_id || !createForm.target_value) { toast.error("All fields are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/targets", createForm);
      toast.success("Target created — Manager notified");
      setShowCreate(false);
      loadTargets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Detail
  const openDetail = async (targetId) => {
    setDetailLoading(true);
    setExpandedChannels({});
    try {
      const [{ data: t }, { data: chData }] = await Promise.all([
        api.get(`/targets/${targetId}`),
        api.get(`/targets/${targetId}/channels`),
      ]);
      setDetail(t);
      setChannelData(chData.channels || []);
      setDetailMeta({ target_value: chData.target_value || 0, grand_total: chData.grand_total || 0 });
      setManagerNotes(t.manager_notes || "");
      setAdminNotes(t.admin_notes || "");
    } catch (err) { toast.error("Failed to load target"); }
    finally { setDetailLoading(false); }
  };

  // Auto-save: collect all SKU targets from channelData and save
  const doAutoSave = async () => {
    if (!detail) return;
    setSaveStatus("saving");
    try {
      const allSkuTargets = [];
      channelData.forEach(ch => {
        (ch.skus || []).forEach(sk => {
          if (sk.target_qty > 0) {
            allSkuTargets.push({ channel_id: ch.channel_id, channel_name: ch.channel_name, sku_id: sk.sku_id, sku_name: sk.sku_name, target_qty: sk.target_qty, selling_price: sk.selling_price });
          }
        });
      });
      await api.put(`/targets/${detail._id}`, { sku_targets: allSkuTargets, manager_notes: managerNotes, admin_notes: adminNotes });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch { setSaveStatus(""); }
  };

  const saveChannel = async (chId) => {
    setChannelSaveStatus(prev => ({ ...prev, [chId]: "saving" }));
    await doAutoSave();
    setChannelSaveStatus(prev => ({ ...prev, [chId]: "saved" }));
    setTimeout(() => setChannelSaveStatus(prev => ({ ...prev, [chId]: "" })), 2000);
  };

  const collectAllSkuTargets = () => {
    const all = [];
    channelData.forEach(ch => { (ch.skus || []).forEach(sk => { if (sk.target_qty > 0) all.push({ channel_id: ch.channel_id, channel_name: ch.channel_name, sku_id: sk.sku_id, sku_name: sk.sku_name, target_qty: sk.target_qty, selling_price: sk.selling_price }); }); });
    return all;
  };

  const triggerAutoSave = () => {
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    setAutoSaveTimer(setTimeout(doAutoSave, 500));
  };

  const updateSkuQty = (chIdx, skuIdx, qty) => {
    const chs = [...channelData];
    const sk = { ...chs[chIdx].skus[skuIdx], target_qty: qty, target_value: round2(qty * chs[chIdx].skus[skuIdx].selling_price) };
    chs[chIdx] = { ...chs[chIdx], skus: [...chs[chIdx].skus] };
    chs[chIdx].skus[skuIdx] = sk;
    chs[chIdx].subtotal = round2(chs[chIdx].skus.reduce((s, r) => s + r.selling_price * r.target_qty, 0));
    chs[chIdx].sku_count = chs[chIdx].skus.filter(r => r.target_qty > 0).length;
    setChannelData(chs);
    setDetailMeta(prev => ({ ...prev, grand_total: round2(chs.reduce((s, c) => s + c.subtotal, 0)) }));
    triggerAutoSave();
  };

  const round2 = (n) => Math.round((n || 0) * 100) / 100;

  const submitTarget = async () => {
    if (!detail) return;
    await doAutoSave();
    try {
      await api.put(`/targets/${detail._id}/submit`);
      toast.success("Target submitted for approval");
      openDetail(detail._id);
      loadTargets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const approveTarget = async () => {
    if (!detail) return;
    try {
      await api.put(`/targets/${detail._id}/approve`);
      toast.success("Target approved");
      openDetail(detail._id);
      loadTargets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const returnTarget = async () => {
    if (!detail || !adminNotes.trim()) { toast.error("Admin notes are required"); return; }
    if (!window.confirm("Return to manager with your suggested changes?")) return;
    try {
      await api.put(`/targets/${detail._id}/return`, { admin_notes: adminNotes, sku_targets: collectAllSkuTargets() });
      toast.success("Target returned to manager with suggestions");
      openDetail(detail._id);
      loadTargets();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const canEdit = detail && ((detail.status === "draft" || detail.status === "returned_by_admin") && (isAdmin || isMgr || detail.sales_manager_id === user?._id || detail.sales_manager_name === user?.name) || (isAdmin && detail.status === "submitted_to_admin"));
  const grandTotal = detailMeta.grand_total;
  const targetVal = detailMeta.target_value;
  const coverage = targetVal > 0 ? Math.min(100, grandTotal / targetVal * 100) : 0;
  const remaining = Math.max(0, targetVal - grandTotal);

  // Detail view
  if (detail) {
    const d = detail;
    return (
      <div data-testid="target-detail-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => { setDetail(null); setChannelData([]); }} className="h-8 w-8"><ArrowLeft size={16} /></Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold">{d.trade_format}</h1>
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${STATUS_COLORS[d.status] || ''}`}>{STATUS_LABELS[d.status] || d.status}</span>
              {saveStatus === "saving" && <span className="text-[10px] text-zinc-400 animate-pulse">Saving...</span>}
              {saveStatus === "saved" && <span className="text-[10px] text-green-600">Saved</span>}
            </div>
            <p className="text-xs text-zinc-500 mt-1">{d.sales_manager_name} &middot; {d.period_label}</p>
          </div>
          <div className="flex gap-2">
            {canEdit && grandTotal >= targetVal && (d.status === "draft" || d.status === "returned_by_admin") && (
              <Button onClick={submitTarget} className="rounded-sm h-8 text-xs bg-blue-600 hover:bg-blue-700" data-testid="submit-target"><Send size={12} /> Submit for Approval</Button>
            )}
            {isAdmin && d.status === "submitted_to_admin" && (
              <>
                <Button onClick={approveTarget} className="rounded-sm h-8 text-xs bg-green-600 hover:bg-green-700" data-testid="approve-target"><Check size={12} /> Approve</Button>
                <Button onClick={returnTarget} variant="outline" className="rounded-sm h-8 text-xs border-orange-300 text-orange-700" data-testid="return-target"><RotateCcw size={12} /> Return</Button>
              </>
            )}
          </div>
        </div>

        {/* Sticky summary bar */}
        <div className="sticky top-0 z-10 bg-white border border-zinc-200 p-3 rounded-sm mb-4 shadow-sm" data-testid="target-summary-bar">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div><span className="text-zinc-400 block text-[10px]">Admin Target</span><span className="font-mono font-bold text-lg">{fmt(targetVal)}</span></div>
            <div><span className="text-zinc-400 block text-[10px]">SKU Value</span><span className={`font-mono font-bold text-lg ${grandTotal >= targetVal ? 'text-green-700' : 'text-amber-600'}`}>{fmt(grandTotal)}</span></div>
            <div><span className="text-zinc-400 block text-[10px]">Remaining</span><span className={`font-mono font-bold text-lg ${remaining <= 0 ? 'text-green-700' : 'text-red-600'}`}>{remaining <= 0 ? 'Covered' : fmt(remaining)}</span></div>
            <div className="md:col-span-2">
              <span className="text-zinc-400 block text-[10px]">Coverage {coverage.toFixed(1)}%</span>
              <div className="w-full bg-zinc-100 rounded-full h-3 mt-1"><div className={`h-full rounded-full transition-all ${coverage >= 100 ? 'bg-green-500' : 'bg-amber-400'}`} style={{ width: `${Math.min(100, coverage)}%` }} /></div>
            </div>
          </div>
        </div>

        {/* Channel accordion */}
        <div className="space-y-2 mb-4" data-testid="channel-accordion">
          {channelData.length === 0 && <p className="text-xs text-zinc-400 text-center py-8 border border-dashed border-zinc-200 rounded-sm">No Sales Channels found for this Trade Format. Check that channels have the correct type set.</p>}
          {channelData.map((ch, chIdx) => {
            const isOpen = expandedChannels[ch.channel_id] !== false;
            return (
              <div key={ch.channel_id} className="border border-zinc-200 rounded-sm" data-testid={`channel-${ch.channel_id}`}>
                <button onClick={() => setExpandedChannels(prev => ({ ...prev, [ch.channel_id]: !isOpen }))}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors text-left">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-zinc-900">{ch.channel_name}</span>
                    <span className="text-[10px] text-zinc-500">{ch.sku_count} of {ch.total_skus} SKUs targeted</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-zinc-700">{fmt(ch.subtotal)}</span>
                    <svg className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-zinc-200">
                    <table className="data-table w-full">
                      <thead><tr><th>SKU</th><th className="num">Selling Price</th><th className="num w-28">Target Qty</th><th className="num">Target Value</th>{canViewPnl && <th className="num">COGS</th>}{canViewPnl && <th className="num">Margin %</th>}</tr></thead>
                      <tbody>
                        {(ch.skus || []).map((sk, skIdx) => {
                          const rowVal = round2(sk.selling_price * sk.target_qty);
                          const margin = sk.selling_price > 0 && sk.base_cost > 0 ? round2((sk.selling_price - sk.base_cost) / sk.selling_price * 100) : 0;
                          const hasSuggestion = sk.admin_suggested_qty !== undefined && sk.admin_suggested_qty !== null && sk.original_manager_qty !== undefined;
                          const suggDiff = hasSuggestion ? sk.admin_suggested_qty - sk.original_manager_qty : 0;
                          const isNew = sk.is_new;
                          const isDisc = sk.is_discontinued;
                          const canEditThisRow = isDisc ? false : (canEdit || (isAdmin && detail?.status === "approved"));
                          return (
                            <tr key={sk.sku_id} className={`${sk.target_qty > 0 ? '' : 'opacity-50'} ${isDisc ? 'bg-zinc-50' : ''}`}>
                              <td className="text-xs">
                                {sk.sku_name} <span className="text-zinc-400 font-mono text-[9px]">{sk.sku_id}</span>
                                {isNew && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-green-100 text-green-700 rounded-sm">New</span>}
                                {isDisc && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-zinc-200 text-zinc-500 rounded-sm">Discontinued</span>}
                                {hasSuggestion && suggDiff > 0 && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-green-100 text-green-700 rounded-sm">+{suggDiff} from admin</span>}
                                {hasSuggestion && suggDiff < 0 && <span className="ml-1 px-1 py-0.5 text-[8px] font-bold bg-red-100 text-red-700 rounded-sm">{suggDiff} from admin</span>}
                              </td>
                              <td className="num font-mono text-xs text-zinc-500">{fmt(sk.selling_price)}</td>
                              <td className="num">{canEditThisRow ? (
                                <Input type="number" min={0} value={sk.target_qty || ""} onChange={e => updateSkuQty(chIdx, skIdx, parseInt(e.target.value) || 0)}
                                  className="h-7 w-24 text-xs text-right rounded-sm font-mono" placeholder="0" />
                              ) : <span className="font-mono text-xs">{sk.target_qty || 0}</span>}</td>
                              <td className="num font-mono text-xs font-semibold">{sk.target_qty > 0 ? fmt(rowVal) : '-'}</td>
                              {canViewPnl && <td className="num font-mono text-xs text-zinc-500">{fmt(sk.base_cost)}</td>}
                              {canViewPnl && <td className="num font-mono text-xs">{margin > 0 ? `${margin}%` : '-'}</td>}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(canEdit || (isAdmin && detail?.status === "approved")) && (
                      <div className="px-4 py-2 border-t border-zinc-100 flex justify-end">
                        <Button variant="outline" onClick={() => saveChannel(ch.channel_id)}
                          className="rounded-sm h-7 text-[10px]" data-testid={`save-channel-${ch.channel_id}`}>
                          {channelSaveStatus[ch.channel_id] === "saving" ? "Saving..." : channelSaveStatus[ch.channel_id] === "saved" ? `Saved ${ch.channel_name} \u2713` : `Save ${ch.channel_name}`}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div><Label className="text-xs">Manager Notes</Label><Textarea value={managerNotes} onChange={e => { setManagerNotes(e.target.value); triggerAutoSave(); }} className="mt-1 rounded-sm text-xs" rows={3} readOnly={!canEdit && !isMgr} /></div>
          <div><Label className="text-xs">Admin Notes</Label><Textarea value={adminNotes} onChange={e => { setAdminNotes(e.target.value); if (isAdmin) triggerAutoSave(); }} className="mt-1 rounded-sm text-xs" rows={3} readOnly={!isAdmin} /></div>
        </div>

        {/* History Timeline */}
        <div className="mb-4" data-testid="target-history">
          <h3 className="text-xs font-semibold text-zinc-600 uppercase mb-2">History</h3>
          <div className="border-l-2 border-zinc-200 ml-2 space-y-0">
            {(d.status_history && d.status_history.length > 0 ? d.status_history : [{ action: "Target Created", by: d.sales_manager_name || "Admin", at: d.created_at, notes: "" }]).map((h, i) => {
              const icons = { "Target Created": "bg-zinc-500", "Submitted to Admin": "bg-blue-500", "Returned by Admin": "bg-orange-500", "Resubmitted": "bg-blue-400", "Approved": "bg-green-500" };
              const dt = h.at || "";
              const fmtHist = (ts) => { if (!ts || ts.length < 16) return ts || ""; const p = ts.slice(0,10).split("-"); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]} ${ts.slice(11,16)}` : ts; };
              return (
                <div key={i} className="relative pl-6 pb-4">
                  <div className={`absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full ${icons[h.action] || 'bg-zinc-400'}`} />
                  <p className="text-xs font-semibold text-zinc-800">{h.action}</p>
                  <p className="text-[10px] text-zinc-500">{h.by} &middot; {fmtHist(dt)}</p>
                  {h.notes && <p className="text-[10px] text-zinc-600 mt-0.5 bg-zinc-50 p-1.5 rounded-sm border border-zinc-100">{h.notes}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit button at bottom */}
        {canEdit && (d.status === "draft" || d.status === "returned_by_admin") && (
          <div className="border-t border-zinc-200 pt-4">
            <Button onClick={submitTarget} disabled={grandTotal < targetVal} className={`rounded-sm h-10 text-sm w-full ${grandTotal >= targetVal ? 'bg-green-600 hover:bg-green-700' : 'bg-zinc-300 cursor-not-allowed'}`} data-testid="submit-target-bottom"
              title={grandTotal < targetVal ? `Add more quantities to meet the ${fmt(targetVal)} target before submitting` : "Submit for Admin approval"}>
              <Send size={14} /> {grandTotal >= targetVal ? "Submit for Approval" : `${fmt(remaining)} more needed to submit`}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="sales-targets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Sales Targets</h1>
          <p className="text-sm text-zinc-500 mt-1">Set, track and monitor sales targets across Trade Formats</p>
        </div>
        {canManage && <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="new-target-btn"><Plus size={14} /> Set New Target</Button>}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div><Label className="text-[10px] text-zinc-500">Trade Format</Label>
          <Select value={filterTF || "all"} onValueChange={v => setFilterTF(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8 text-xs rounded-sm"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Trade Formats</SelectItem>{TRADE_FORMATS.map(tf => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] text-zinc-500">From</Label><Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="h-8 text-xs rounded-sm w-36" /></div>
        <div><Label className="text-[10px] text-zinc-500">To</Label><Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="h-8 text-xs rounded-sm w-36" /></div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList className="bg-zinc-100 rounded-sm h-8">
          <TabsTrigger value="targets" className="text-xs rounded-sm h-6">Targets</TabsTrigger>
          <TabsTrigger value="performance" className="text-xs rounded-sm h-6">Performance</TabsTrigger>
        </TabsList>

        {/* ═══ TARGETS TAB ═══ */}
        <TabsContent value="targets">
          <div className="border border-zinc-200 overflow-x-auto" data-testid="targets-table">
            <table className="data-table w-full">
              <thead><tr><th>Trade Format</th><th>Sales Manager</th><th>Period</th><th className="num">Target Value</th><th className="num">SKUs</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
                targets.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">No targets found</td></tr> :
                targets.map(t => (
                  <tr key={t._id} data-testid={`target-row-${t._id}`}>
                    <td className="text-xs font-medium">{t.trade_format}</td>
                    <td className="text-xs">{t.sales_manager_name}</td>
                    <td className="text-xs font-mono">{t.period_label}</td>
                    <td className="num font-mono text-xs font-semibold">{fmt(t.target_value)}</td>
                    <td className="num text-xs">{(t.sku_targets || []).length}</td>
                    <td><span className={`px-2 py-0.5 text-[9px] font-semibold rounded-sm ${STATUS_COLORS[t.status] || ''}`}>{STATUS_LABELS[t.status] || t.status}</span></td>
                    <td><Button variant="outline" onClick={() => openDetail(t._id)} className="rounded-sm h-6 text-[10px] px-2">View</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ═══ PERFORMANCE TAB ═══ */}
        <TabsContent value="performance">
          {perfLoading ? <p className="text-xs text-zinc-400 text-center py-8">Loading...</p> : perfData ? (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="perf-summary">
                <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Total Target</p><p className="text-xl font-mono font-bold">{fmt(perfData.summary?.total_target)}</p></div>
                <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Total Actual</p><p className="text-xl font-mono font-bold">{fmt(perfData.summary?.total_actual)}</p></div>
                <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Achievement</p><p className="text-xl font-mono font-bold">{perfData.summary?.achievement || 0}%</p></div>
                <div className="border border-zinc-200 p-3 rounded-sm"><p className="text-[10px] text-zinc-400 uppercase">Status</p><span className={`px-2 py-1 text-xs font-semibold rounded-sm ${TL_COLORS[perfData.summary?.traffic_light] || ''}`}>{(perfData.summary?.traffic_light || "").toUpperCase()}</span></div>
              </div>
              {/* Breakdown table */}
              <div className="border border-zinc-200 overflow-x-auto" data-testid="perf-breakdown">
                <table className="data-table w-full">
                  <thead><tr><th>Trade Format</th><th>Manager</th><th>Period</th><th className="num">Target</th><th className="num">Actual</th><th className="num">Achievement</th><th className="num">Variance</th><th>TL</th></tr></thead>
                  <tbody>
                    {(perfData.breakdown || []).length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">No approved targets for this period</td></tr> :
                    (perfData.breakdown || []).map((b, i) => (
                      <tr key={i} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDetail(b._id)}>
                        <td className="text-xs font-medium">{b.trade_format}</td>
                        <td className="text-xs">{b.sales_manager_name}</td>
                        <td className="text-xs font-mono">{b.period_label}</td>
                        <td className="num font-mono text-xs">{fmt(b.target_value)}</td>
                        <td className="num font-mono text-xs">{fmt(b.actual_value)}</td>
                        <td className="num font-mono text-xs font-semibold">{b.achievement}%</td>
                        <td className={`num font-mono text-xs ${b.variance >= 0 ? 'text-green-700' : 'text-red-600'}`}>{b.variance >= 0 ? '+' : ''}{fmt(b.variance)}</td>
                        <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${TL_COLORS[b.traffic_light] || ''}`}>{b.traffic_light}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : <p className="text-xs text-zinc-400 text-center py-8">Select a period and click Refresh</p>}
        </TabsContent>
      </Tabs>

      {/* Create Target Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-target-dialog">
          <DialogHeader><DialogTitle className="font-heading">Set New Target</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Trade Format *</Label>
              <Select value={createForm.trade_format || "none"} onValueChange={v => setCreateForm({ ...createForm, trade_format: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- Select --</SelectItem>{TRADE_FORMATS.map(tf => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Sales Manager *</Label>
              <Select value={createForm.sales_manager_id || "none"} onValueChange={v => setCreateForm({ ...createForm, sales_manager_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- Select --</SelectItem>{managers.map(m => <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Period Type</Label>
                <Select value={createForm.period_type} onValueChange={v => setCreateForm({ ...createForm, period_type: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Target Value (INR) *</Label><Input type="number" value={createForm.target_value} onChange={e => setCreateForm({ ...createForm, target_value: parseFloat(e.target.value) || "" })} className="mt-1 rounded-sm" placeholder="e.g. 500000" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Period Start</Label><Input type="date" value={createForm.period_start} onChange={e => setCreateForm({ ...createForm, period_start: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Period End</Label><Input type="date" value={createForm.period_end} onChange={e => setCreateForm({ ...createForm, period_end: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={saveCreate} disabled={saving} className="rounded-sm text-xs" data-testid="save-target-btn">{saving ? "Saving..." : "Create Target"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
